import React, { createContext, useContext, useMemo } from 'react';

import { useHighlightInteraction } from '../../hooks/useHighlightInteraction';
import type { UploadedReceipt } from '../../types';
import { enrichLineItemMappings } from '../../utils/ocr-field-mapping-utils';

import InvoicePreviewPanel from './InvoicePreviewPanel';

/** Context for booking form fields to consume highlight state */
interface HighlightContextValue {
  activeFieldId: string | null;
  onFieldFocus: (fieldId: string) => void;
  clearHighlights: () => void;
}

const HighlightContext = createContext<HighlightContextValue | null>(null);

/** Use inside BookingFormFields to get highlight state from SplitPanelBookingLayout */
export const useFieldHighlight = (): HighlightContextValue | null =>
  useContext(HighlightContext);

interface SplitPanelBookingLayoutProps {
  receipt: UploadedReceipt;
  children: React.ReactNode;
}

/**
 * Split-panel layout: invoice preview (left) + booking form (right).
 * Falls back to children-only when receipt has no ocrData.
 * Wires bidirectional highlighting via useHighlightInteraction hook.
 */
const SplitPanelBookingLayout: React.FC<SplitPanelBookingLayoutProps> = ({
  receipt,
  children,
}) => {
  // Enrich line item mappings for receipts processed before backend mapping
  const enrichedOcrData = useMemo(
    () => enrichLineItemMappings(receipt.ocrData),
    [receipt.ocrData]
  );

  const highlight = useHighlightInteraction(enrichedOcrData);

  const contextValue = useMemo(
    () => ({
      activeFieldId: highlight.activeFieldId,
      onFieldFocus: highlight.onFieldFocus,
      clearHighlights: highlight.clearHighlights,
    }),
    [highlight.activeFieldId, highlight.onFieldFocus, highlight.clearHighlights]
  );

  if (!receipt.ocrData) {
    return <>{children}</>;
  }

  return (
    <HighlightContext.Provider
      value={contextValue}
    >
      <div className='flex flex-col md:flex-row gap-4 md:max-h-[70vh]'>
        {/* Left: Invoice preview — scrolls independently */}
        <div className='w-full md:w-1/2 overflow-auto'>
          <InvoicePreviewPanel
            storageKey={receipt.storageKey}
            contentType={receipt.contentType}
            fileName={receipt.originalFileName}
            ocrData={enrichedOcrData}
            activeFieldIds={highlight.activeHighlightIds}
            onHighlightClick={highlight.onHighlightClick}
          />
        </div>
        {/* Right: Booking form/table — scrolls independently */}
        <div className='w-full md:w-1/2 overflow-auto'>{children}</div>
      </div>
    </HighlightContext.Provider>
  );
};

export default SplitPanelBookingLayout;
